import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { first_name: "", last_name: "", email: "", message: "" };

export const ContactForm = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleClose = (v) => {
    setOpen(v);
    if (!v) {
      setTimeout(() => {
        setForm(empty);
        setSent(false);
        setError("");
      }, 200);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="contact-section" className="py-16 lg:py-20">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Mail size={28} strokeWidth={1.4} className="mx-auto mb-5" style={{ color: "#B89968" }} />
        <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight" style={{ color: "#2C2A28" }}>
          Have a question? Get in touch.
        </h2>
        <p className="mt-4 text-base leading-relaxed" style={{ color: "#4A4744" }}>
          Send us a message and one of our operators will get back to you shortly.
        </p>
        <button
          data-testid="open-contact-form-btn"
          onClick={() => setOpen(true)}
          className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full font-medium transition-all duration-300"
          style={{ backgroundColor: "#7B1E3B", color: "#F0E6D2" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#641730")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#7B1E3B")}
        >
          Contact Us <Send size={17} />
        </button>
      </div>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent data-testid="contact-form-dialog" className="sm:max-w-md" style={{ backgroundColor: "#FAF6EE" }}>
          {sent ? (
            <motion.div
              data-testid="contact-success-message"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-8 text-center"
            >
              <CheckCircle2 size={48} strokeWidth={1.4} className="mx-auto mb-5" style={{ color: "#9CAF88" }} />
              <p className="font-serif-display text-xl leading-snug" style={{ color: "#2C2A28" }}>
                Thank you for your message one of our operators will answer you shortly.
              </p>
            </motion.div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl font-normal" style={{ color: "#2C2A28" }}>
                  Send us a message
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    data-testid="contact-first-name"
                    placeholder="First name"
                    required
                    value={form.first_name}
                    onChange={update("first_name")}
                  />
                  <Input
                    data-testid="contact-last-name"
                    placeholder="Last name"
                    required
                    value={form.last_name}
                    onChange={update("last_name")}
                  />
                </div>
                <Input
                  data-testid="contact-email"
                  type="email"
                  placeholder="Email"
                  required
                  value={form.email}
                  onChange={update("email")}
                />
                <Textarea
                  data-testid="contact-message"
                  placeholder="Your message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                />
                {error && (
                  <p data-testid="contact-error" className="text-sm" style={{ color: "#7B1E3B" }}>
                    {error}
                  </p>
                )}
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 disabled:opacity-60"
                  style={{ backgroundColor: "#2C2A28", color: "#F0E6D2" }}
                >
                  {sending ? "Sending..." : "Send message"} <Send size={16} />
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactForm;
